import { useGetBrokersMembersMemberId } from 'utils/web_api'

import { formatBoolean, getFormattedDate } from '../../utils'
import { CollapsibleDataContainer } from '../reusable/CollapsibleDataContainer'
import { CopyFieldRow } from '../reusable/CopyFieldRow'
import { SimpleFieldRow } from '../reusable/SimpleFieldRow'
import { SkeletonItem } from '../reusable/SkeletonItem'

const Heading = () => (
  <div className='flex items-center space-x-2'>
    <span className='heading-md'>Member information</span>
  </div>
)

const SkeletonFieldRow = ({ labelWidth = 'w-1/3', valueWidth = 'w-3/4' }: { labelWidth?: string; valueWidth?: string }) => (
  <div className='px-4 py-2 sm:grid sm:grid-cols-3 sm:gap-4 sm:px-0'>
    <dt className='text-sm font-medium leading-6 text-gray-900'>
      <SkeletonItem widthClass={labelWidth} />
    </dt>
    <dd className='mt-1 text-sm leading-6 text-gray-700 sm:col-span-2 sm:mt-0'>
      <SkeletonItem widthClass={valueWidth} />
    </dd>
  </div>
)

const widths = ['w-1/4', 'w-1/3', 'w-2/5', 'w-3/5', 'w-4/5', 'w-1/2', 'w-3/4']
const getRandomWidth = () => {
  return widths[Math.floor(Math.random() * widths.length)]
}

const MemberInformationSkeleton = () => (
  <dl data-testid='MemberInformationSkeleton' className='divide-y divide-gray-100 p-4'>
    <SkeletonFieldRow labelWidth={getRandomWidth()} valueWidth={getRandomWidth()} />
    <SkeletonFieldRow labelWidth={getRandomWidth()} valueWidth={getRandomWidth()} />
    <SkeletonFieldRow labelWidth={getRandomWidth()} valueWidth={getRandomWidth()} />
    <SkeletonFieldRow labelWidth={getRandomWidth()} valueWidth={getRandomWidth()} />
    <SkeletonFieldRow labelWidth={getRandomWidth()} valueWidth={getRandomWidth()} />
    <SkeletonFieldRow labelWidth={getRandomWidth()} valueWidth={getRandomWidth()} />
    <SkeletonFieldRow labelWidth={getRandomWidth()} valueWidth={getRandomWidth()} />
    <SkeletonFieldRow labelWidth={getRandomWidth()} valueWidth={getRandomWidth()} />
    <SkeletonFieldRow labelWidth={getRandomWidth()} valueWidth={getRandomWidth()} />
    <SkeletonFieldRow labelWidth={getRandomWidth()} valueWidth={getRandomWidth()} />
    <SkeletonFieldRow labelWidth={getRandomWidth()} valueWidth={getRandomWidth()} />
    <SkeletonFieldRow labelWidth={getRandomWidth()} valueWidth={getRandomWidth()} />
    <SkeletonFieldRow labelWidth={getRandomWidth()} valueWidth={getRandomWidth()} />
  </dl>
)

interface MemberInformationProps {
  memberId: string
}

export default function MemberInformation ({
  memberId,
}: MemberInformationProps) {
  const { data: member, isLoading, error } = useGetBrokersMembersMemberId(memberId, {
    query: {
      enabled: !!memberId,
      refetchOnWindowFocus: false,
    },
  })

  const formattedDateOfBirth = member?.date_of_birth && getFormattedDate(member.date_of_birth, { day: '2-digit', month: '2-digit', year: 'numeric', timeZone: 'UTC' })

  const successContent = (
    <dl className='divide-y divide-gray-100 p-4'>
      <CopyFieldRow label='ID' value={member?.id} omitIfUndefined />

      <CopyFieldRow label='Legal name' value={member?.legal_full_name} omitIfUndefined />

      <CopyFieldRow label='Date of birth' value={formattedDateOfBirth} omitIfUndefined />

      {member?.preferred_name &&
        member?.preferred_name !== member?.given_name && (
          <CopyFieldRow
            label='Preferred name'
            value={member?.preferred_name}
            omitIfUndefined
          />
      )}

      <CopyFieldRow
        isPhoneNumber
        label='Phone number'
        value={member?.phone_number}
        omitIfUndefined
      />

      <CopyFieldRow label='Email address' value={member?.email} omitIfUndefined />

      <CopyFieldRow label='Address line 1' value={member?.address_line1} omitIfUndefined />

      <CopyFieldRow label='Address line 2' value={member?.address_line2} omitIfUndefined />

      <CopyFieldRow label='City' value={member?.city} omitIfUndefined />

      <CopyFieldRow label='State' value={member?.state} omitIfUndefined />

      <CopyFieldRow label='Zip' value={member?.zip} omitIfUndefined />

      <CopyFieldRow label='County' value={member?.county_name} omitIfUndefined />

      <SimpleFieldRow
        label='Paperless Billing Opt-In'
        value={formatBoolean(member?.paperless_opt_in)}
        omitIfUndefined
      />

      <SimpleFieldRow
        label='Paperless Renewal Notice Opt-In'
        value={formatBoolean(member?.paperless_renewals_opt_in)}
        omitIfUndefined
      />

      <CopyFieldRow label='Suggested Password' value={member?.suggested_password} omitIfUndefined />

      <CopyFieldRow label='Personal Email' value={member?.employee_personal_email} omitIfUndefined />

      <CopyFieldRow label='Work Email' value={member?.employee_work_email} omitIfUndefined />
    </dl>
  )

  return (
    <CollapsibleDataContainer
      heading={<Heading />}
      isLoading={isLoading}
      error={!!error || !member}
      localStorageKey='memberInformationOpenByDefault'
      loadingContent={<MemberInformationSkeleton />}
      errorContent={
        <div className='p-4 text-center text-red-500'>
          Error loading member information
        </div>
      }
      successContent={successContent}
    />
  )
}
